import { lazy, Suspense, type ReactNode } from 'react';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { store } from '@/store';
import { toastDefaults } from '@/core/toast/notify';
import { queryClient } from '@/lib/query/queryClient';
import { env } from '@/config/env';
import { EntityApprovalTimelineProvider } from '@/modules/approvals/components/EntityApprovalTimelineProvider';
import { I18nProvider } from '@/i18n/I18nProvider';

const RealtimeProvider = lazy(() =>
  import('@/lib/realtime/RealtimeProvider').then((m) => ({ default: m.RealtimeProvider })),
);

function RealtimeLayer({ children }: { children: ReactNode }) {
  if (!env.realtimeEnabled) {
    return <>{children}</>;
  }
  return (
    <Suspense fallback={children}>
      <RealtimeProvider>{children}</RealtimeProvider>
    </Suspense>
  );
}

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <Provider store={store}>
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          <EntityApprovalTimelineProvider>
            <RealtimeLayer>{children}</RealtimeLayer>
          </EntityApprovalTimelineProvider>
        </QueryClientProvider>
        <Toaster
          position="top-right"
          toastOptions={toastDefaults}
          containerStyle={{ top: 16, right: 16 }}
          gutter={12}
        />
      </I18nProvider>
    </Provider>
  );
}
